import React, { createContext } from 'react';

import { Country, District, State } from '@nw/shared/config';

interface StateAndDistrictContextTypes {
  countries: React.MutableRefObject<Array<Country>>;
  selectedCountry: React.MutableRefObject<Country>;
  updateCountries: (countries: Array<Country>) => void;
  updateSelectedCountry: (country: Country) => void;
  states: React.MutableRefObject<Array<State>>;
  selectedState: React.MutableRefObject<State>;
  updateStates: (states: Array<State>) => void;
  updateSelectedState: (state: State) => void;
  districts: React.MutableRefObject<Array<District>>;
  selectedDistrict: React.MutableRefObject<District>;
  updateDistricts: (states: Array<District>) => void;
  updateSelectedDistrict: (state: District) => void;
}

const StateAndDistrictContext =
  createContext<StateAndDistrictContextTypes | null>({
    countries: { current: [] },
    selectedCountry: { current: {} as Country },
    updateCountries: () => {},
    updateSelectedCountry: () => {},
    states: { current: [] },
    selectedState: { current: {} as State },
    updateStates: () => {},
    updateSelectedState: () => {},
    districts: { current: [] },
    selectedDistrict: { current: {} as District },
    updateDistricts: () => {},
    updateSelectedDistrict: () => {}
  });

export const useStateAndDistricts = () => {
  const context = React.useContext(StateAndDistrictContext);

  if (!context)
    throw new Error('Expected to be wrapped in a StateAndDistrictProvider');

  return context;
};

export const StateAndDistrictsProvider = ({
  children
}: {
  children: React.ReactChild;
}) => {
  const countries = React.useRef<Array<Country>>([]);
  const country = React.useRef<Country>({} as Country);
  const states = React.useRef<Array<State>>([]);
  const state = React.useRef<State>({} as State);
  const districts = React.useRef<Array<District>>([]);
  const district = React.useRef<District>({} as District);

  const updateCountries = (latestCountries: Array<Country>) => {
    countries.current = latestCountries;
  };

  const updateSelectedCountry = (selectedCountry: Country) => {
    country.current = selectedCountry;
  };

  const updateStates = (latestStates: Array<State>) => {
    states.current = latestStates;
  };

  const updateSelectedState = (selectedState: State) => {
    state.current = selectedState;
  };

  const updateDistricts = (latestDistricts: Array<District>) => {
    districts.current = latestDistricts;
  };

  const updateSelectedDistrict = (selectedDistrict: District) => {
    district.current = selectedDistrict;
  };

  return (
    <StateAndDistrictContext.Provider
      value={{
        countries,
        selectedCountry: country,
        updateCountries,
        updateSelectedCountry,
        states,
        selectedState: state,
        updateStates,
        updateSelectedState,
        districts,
        selectedDistrict: district,
        updateDistricts,
        updateSelectedDistrict
      }}
    >
      {children}
    </StateAndDistrictContext.Provider>
  );
};
